import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { isNativeApp } from '../utils/pwa';
import { Button } from './ui/Button';
import { Checkbox } from './ui/Checkbox';

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  consentDate: string;
  consentVersion: string;
}

const CONSENT_VERSION = '1.0';
const STORAGE_KEY = 'cookie_consent';

export const getCookiePreferences = (): CookiePreferences | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const prefs = JSON.parse(stored) as CookiePreferences;
      if (prefs.consentVersion !== CONSENT_VERSION) {
        return null;
      }
      return prefs;
    }
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
  return null;
};

export const saveCookiePreferences = (prefs: Omit<CookiePreferences, 'consentDate' | 'consentVersion'>): void => {
  const fullPrefs: CookiePreferences = {
    ...prefs,
    essential: true,
    consentDate: new Date().toISOString(),
    consentVersion: CONSENT_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(fullPrefs));
  window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: fullPrefs }));
};

export const isAnalyticsEnabled = (): boolean => {
  const prefs = getCookiePreferences();
  return prefs?.analytics ?? false;
};

export const isMarketingEnabled = (): boolean => {
  const prefs = getCookiePreferences();
  return prefs?.marketing ?? false;
};

export const clearCookieConsent = (): void => {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent('cookieConsentCleared'));
};

interface CookieConsentBannerProps {
  onConsentGiven?: (preferences: CookiePreferences) => void;
}

const CookieConsentBanner: React.FC<CookieConsentBannerProps> = ({ onConsentGiven }) => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [marketingEnabled, setMarketingEnabled] = useState(false);

  useEffect(() => {
    if (isNativeApp()) return;
    const existingConsent = getCookiePreferences();
    if (!existingConsent) {
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    saveCookiePreferences({ essential: true, analytics: true, marketing: true });
    setVisible(false);
    onConsentGiven?.(getCookiePreferences()!);
  };

  const handleRejectNonEssential = () => {
    saveCookiePreferences({ essential: true, analytics: false, marketing: false });
    setVisible(false);
    onConsentGiven?.(getCookiePreferences()!);
  };

  const handleSavePreferences = () => {
    saveCookiePreferences({ essential: true, analytics: analyticsEnabled, marketing: marketingEnabled });
    setVisible(false);
    onConsentGiven?.(getCookiePreferences()!);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 bg-bg-secondary border-t border-border-medium shadow-[0_-4px_20px_rgba(0,0,0,0.3)] p-2 pb-[calc(8px+env(safe-area-inset-bottom))] md:p-6 md:pb-6 z-[1000]"
      role="dialog"
      aria-labelledby="cookie-title"
      aria-describedby="cookie-description"
    >
      <div className="max-w-[900px] mx-auto flex flex-col gap-5 md:gap-2">
        {!showCustomize ? (
          <>
            {/* Desktop */}
            <div className="hidden md:flex flex-col gap-3">
              <div className="flex flex-col gap-3">
                <h2 id="cookie-title" className="text-xl md:text-base font-semibold text-text-primary m-0 leading-snug">
                  Cookie Preferences
                </h2>
                <p id="cookie-description" className="text-[15px] md:text-[13px] text-text-secondary leading-relaxed m-0">
                  We use cookies to enhance your experience. Essential cookies are required for the site to function.
                  Analytics cookies help us improve our service. You can customize your preferences or accept all cookies.
                </p>
                <p className="text-sm md:text-[13px] text-text-tertiary m-0">
                  Learn more in our <Link to="/privacy-policy" className="text-accent no-underline hover:underline">Privacy Policy</Link>.
                </p>
              </div>
              <div className="flex flex-wrap gap-3 justify-end pt-1">
                <Button variant="secondary" onClick={handleRejectNonEssential} className="min-w-[140px] md:min-w-0 md:flex-1">
                  Essential Only
                </Button>
                <Button variant="secondary" onClick={() => setShowCustomize(true)} className="border-accent text-accent min-w-[140px] md:min-w-0 md:flex-1">
                  Customize
                </Button>
                <Button variant="primary" onClick={handleAcceptAll} className="min-w-[140px] md:min-w-0 md:flex-1">
                  Accept All
                </Button>
              </div>
            </div>

            {/* Mobile compact */}
            <div className="flex md:hidden items-center gap-2 flex-wrap" aria-label="Cookie Preferences">
              <p className="flex-1 min-w-[140px] text-xs text-text-secondary leading-tight m-0">
                We use cookies.{' '}
                <Button
                  variant="text"
                  onClick={() => setShowCustomize(true)}
                  className="p-0 text-accent text-xs underline font-[inherit] focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                >
                  Customize
                </Button>
              </p>
              <div className="flex gap-1.5 shrink-0">
                <Button variant="secondary" size="sm" onClick={handleRejectNonEssential} className="px-2.5">
                  Essential Only
                </Button>
                <Button variant="primary" size="sm" onClick={handleAcceptAll} className="px-2.5">
                  Accept All
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col gap-3">
              <h2 id="cookie-title" className="text-xl md:text-base font-semibold text-text-primary m-0 leading-snug">
                Customize Cookie Preferences
              </h2>
              <div className="flex flex-col gap-4 mt-4">
                <div className="flex justify-between items-start gap-4 p-4 md:p-3 bg-bg-tertiary rounded-lg border border-border-light flex-col md:flex-row md:items-start">
                  <div className="flex-1">
                    <h3 className="text-base md:text-sm font-semibold text-text-primary m-0 mb-1">Essential Cookies</h3>
                    <p className="text-sm md:text-xs text-text-secondary m-0 leading-snug">Required for basic site functionality, authentication, and security. Cannot be disabled.</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-3 justify-between md:justify-end mt-2 md:mt-0">
                    <Checkbox id="essential-cookies" checked={true} disabled aria-label="Essential cookies (always enabled)" />
                    <label htmlFor="essential-cookies" className="text-sm text-text-tertiary italic cursor-not-allowed">Always On</label>
                  </div>
                </div>

                <div className="flex justify-between items-start gap-4 p-4 md:p-3 bg-bg-tertiary rounded-lg border border-border-light flex-col md:flex-row md:items-start">
                  <div className="flex-1">
                    <h3 className="text-base md:text-sm font-semibold text-text-primary m-0 mb-1">Analytics Cookies</h3>
                    <p className="text-sm md:text-xs text-text-secondary m-0 leading-snug">Help us understand how you use the site so we can improve it. No personal data is shared with third parties.</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-3 justify-between md:justify-end mt-2 md:mt-0">
                    <label className="cookie-toggle-switch relative inline-block w-12 h-[26px] cursor-pointer">
                      <Checkbox id="analytics-cookies" checked={analyticsEnabled} onChange={(e) => setAnalyticsEnabled(e.target.checked)} aria-label="Enable analytics cookies" />
                      <span className="cookie-toggle-slider absolute inset-0 bg-[var(--theme-border-medium)] rounded-full transition-[background-color] duration-300 ease-in-out"></span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-between items-start gap-4 p-4 md:p-3 bg-bg-tertiary rounded-lg border border-border-light flex-col md:flex-row md:items-start">
                  <div className="flex-1">
                    <h3 className="text-base md:text-sm font-semibold text-text-primary m-0 mb-1">Marketing Cookies</h3>
                    <p className="text-sm md:text-xs text-text-secondary m-0 leading-snug">Used to show you relevant content and measure the effectiveness of our communications.</p>
                  </div>
                  <div className="shrink-0 flex items-center gap-3 justify-between md:justify-end mt-2 md:mt-0">
                    <label className="cookie-toggle-switch relative inline-block w-12 h-[26px] cursor-pointer">
                      <Checkbox id="marketing-cookies" checked={marketingEnabled} onChange={(e) => setMarketingEnabled(e.target.checked)} aria-label="Enable marketing cookies" />
                      <span className="cookie-toggle-slider absolute inset-0 bg-[var(--theme-border-medium)] rounded-full transition-[background-color] duration-300 ease-in-out"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 justify-end pt-1">
              <Button variant="secondary" onClick={() => setShowCustomize(false)}>
                Back
              </Button>
              <Button variant="primary" onClick={handleSavePreferences}>
                Save Preferences
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
